// SK Tickets — All API endpoint definitions

// ─── Platform API (Auth & Identity — called DIRECTLY) ─────────
// Auth/identity call skplatform-api DIRECTLY. An earlier design routed them
// through the sk-backbone hub, but the hub only proxied a byte-for-byte copy
// of the platform's contract — adding a hard dependency on the backbone being
// up, for no benefit (and no dev backbone is even provisioned). Calling the
// platform directly removes that dependency and matches SK-Catalog / SK-Upsell
// / platform-fe: one session authority, no indirection.
// Injected at build time via webpack DefinePlugin (PLATFORM_BASE_URL env var).
// Local dev: skplatform-api runs on :3013.
// Stage/Prod: Cloud Build passes --build-arg PLATFORM_BASE_URL=https://...
export const PLATFORM_BASE_URL = process.env.PLATFORM_BASE_URL || 'http://localhost:3013';

// Versioned auth/identity prefix — skplatform-api v1 (direct).
const P = `${PLATFORM_BASE_URL}/v1`;

// Product slug this frontend identifies as to the shared platform-api. Drives
// which app the verification / reset email links point back to, and which
// product the resend-verification call targets. Single source of truth.
export const PRODUCT = 'tickets';

// Login / signup / verification / password-reset / invitations all live on the
// centralized SK Platform (platform-fe) now — this app has no auth flow. Only
// the session-consumption endpoints remain: logout, silent token refresh (used
// by the api-client on 401), and the /me session probe.
export const PLATFORM_AUTH = {
  logout: `${P}/auth/logout`,
  refresh: `${P}/auth/refresh`,
  me: `${P}/me`,
};

// ─── Org / teammate management (RBAC owned by platform-api, called directly) ───
export const PLATFORM_ORG = {
  members: (orgId: string) => `${P}/orgs/${orgId}/members`,
  invitations: (orgId: string) => `${P}/orgs/${orgId}/invitations`,
  invitation: (orgId: string, invitationId: string) =>
    `${P}/orgs/${orgId}/invitations/${invitationId}`,
  resendInvitation: (orgId: string, invitationId: string) =>
    `${P}/orgs/${orgId}/invitations/${invitationId}/resend`,
};

// ─── SK-Tickets API ───────────────────────────────────────────
// Injected at build time via webpack DefinePlugin (TICKETS_BASE_URL env var).
// Local dev: copy .env.example → .env and set values.
// Stage/Prod: set TICKETS_BASE_URL in the CI/CD environment.
export const TICKETS_BASE_URL = process.env.TICKETS_BASE_URL || '';
export const API_BASE = `${TICKETS_BASE_URL}/v1`;

// ─── Tickets ──────────────────────────────────────────────────
export const TICKETS = {
  list: `${API_BASE}/tickets`,
  create: `${API_BASE}/tickets`,
  get: (id: string) => `${API_BASE}/tickets/${id}`,
  byNumber: (n: number) => `${API_BASE}/tickets/by-number/${n}`,
  update: (id: string) => `${API_BASE}/tickets/${id}`,
  delete: (id: string) => `${API_BASE}/tickets/${id}`,
  status: (id: string) => `${API_BASE}/tickets/${id}/status`,
  assign: (id: string) => `${API_BASE}/tickets/${id}/assign`,
  autoAssign: (id: string) => `${API_BASE}/tickets/${id}/auto-assign`,
  merge: (id: string) => `${API_BASE}/tickets/${id}/merge`,
  csat: (id: string) => `${API_BASE}/tickets/${id}/csat`,
  bulkAssign: `${API_BASE}/tickets/bulk/assign`,
  bulkStatus: `${API_BASE}/tickets/bulk/status`,
  bulkTag: `${API_BASE}/tickets/bulk/tag`,
};

// ─── Spaces (INTERNAL / PUBLIC partition) ─────────────────────
// Tickets-local ACL. Read surfaces (list/get/members) are open to any org
// member; lifecycle + membership mutations are org-admin only (enforced
// server-side). The server derives space scope from the authenticated
// principal — `spaceId` is a target, never an authorization input.
export const SPACES = {
  // Spaces the caller may actually enter — already ACL-filtered. Switcher source.
  list: `${API_BASE}/spaces`,
  // Every space in the org (admin management list).
  all: `${API_BASE}/spaces/all`,
  create: `${API_BASE}/spaces`,
  get: (id: string) => `${API_BASE}/spaces/${id}`,
  deactivate: (id: string) => `${API_BASE}/spaces/${id}/deactivate`,
  // Active members ∪ requester — feeds SK Notifications.
  recipients: (id: string) => `${API_BASE}/spaces/${id}/recipients`,
  members: (id: string) => `${API_BASE}/spaces/${id}/members`,
  membershipHistory: (id: string) => `${API_BASE}/spaces/${id}/members/history`,
  grant: (id: string) => `${API_BASE}/spaces/${id}/members`,
  revoke: (id: string, userId: string) => `${API_BASE}/spaces/${id}/members/${userId}`,
};

// ─── Comments ─────────────────────────────────────────────────
export const COMMENTS = {
  list: (ticketId: string) => `${API_BASE}/tickets/${ticketId}/comments`,
  create: (ticketId: string) => `${API_BASE}/tickets/${ticketId}/comments`,
  update: (id: string) => `${API_BASE}/tickets/comments/${id}`,
  remove: (id: string) => `${API_BASE}/tickets/comments/${id}`,
  deliveries: (ticketId: string, commentId: string) =>
    `${API_BASE}/tickets/${ticketId}/comments/${commentId}/deliveries`,
};

// ─── Contacts (callers) ───────────────────────────────────────
export const CONTACTS = {
  list: `${API_BASE}/contacts`,
  create: `${API_BASE}/contacts`,
  update: (id: string) => `${API_BASE}/contacts/${id}`,
  delete: (id: string) => `${API_BASE}/contacts/${id}`,
};

// ─── Agents ───────────────────────────────────────────────────
export const AGENTS = {
  list: `${API_BASE}/agents`,
  create: `${API_BASE}/agents`,
  get: (id: string) => `${API_BASE}/agents/${id}`,
  update: (id: string) => `${API_BASE}/agents/${id}`,
  delete: (id: string) => `${API_BASE}/agents/${id}`,
  myAvailability: `${API_BASE}/agents/me/availability`,
};

// ─── Org Settings ─────────────────────────────────────────────
export const ORG_SETTINGS = {
  get: `${API_BASE}/org/settings`,
  update: `${API_BASE}/org/settings`,
};

// ─── Email Channel ────────────────────────────────────────────
export const EMAIL_CHANNEL = {
  get: `${API_BASE}/org-settings/email-channel`,
  update: `${API_BASE}/org-settings/email-channel`,
  verifySender: `${API_BASE}/org-settings/email-channel/verify-sender`,
  confirmVerify: `${API_BASE}/org-settings/email-channel/verify-sender/confirm`,
};

// ─── Email Templates ──────────────────────────────────────────
export const EMAIL_TEMPLATES = {
  list: `${API_BASE}/email-templates`,
  get: (id: string, locale: string) => `${API_BASE}/email-templates/${id}/${locale}`,
  update: (id: string, locale: string) => `${API_BASE}/email-templates/${id}/${locale}`,
  reset: (id: string, locale: string) => `${API_BASE}/email-templates/${id}/${locale}/reset`,
  preview: (id: string, locale: string) => `${API_BASE}/email-templates/${id}/${locale}/preview`,
};

// ─── Automation Rules ─────────────────────────────────────────
export const AUTOMATIONS = {
  list: `${API_BASE}/automation/rules`,
  create: `${API_BASE}/automation/rules`,
  get: (id: string) => `${API_BASE}/automation/rules/${id}`,
  update: (id: string) => `${API_BASE}/automation/rules/${id}`,
  delete: (id: string) => `${API_BASE}/automation/rules/${id}`,
  test: (id: string) => `${API_BASE}/automation/rules/${id}/test`,
};

// ─── SLA ──────────────────────────────────────────────────────
export const SLA = {
  rules: `${API_BASE}/sla/rules`,
  timers: `${API_BASE}/sla/timers`,
};

// ─── Reports ──────────────────────────────────────────────────
export const REPORTS = {
  volume: `${API_BASE}/reports/volume`,
  csat: `${API_BASE}/reports/csat`,
  slaAttainment: `${API_BASE}/reports/sla-attainment`,
  agentProductivity: `${API_BASE}/reports/agent-productivity`,
  export: `${API_BASE}/reports/export`,
};

// ─── Watchers ─────────────────────────────────────────────────
export const WATCHERS = {
  list: (ticketId: string) => `${API_BASE}/tickets/${ticketId}/watchers`,
  add: (ticketId: string) => `${API_BASE}/tickets/${ticketId}/watchers`,
  remove: (ticketId: string, userId: string) => `${API_BASE}/tickets/${ticketId}/watchers/${userId}`,
};

// ─── Lenses ───────────────────────────────────────────────────
export const LENSES = {
  agent: (ticketId: string) => `${API_BASE}/lens/agent/${ticketId}`,
  customer: (ticketId: string) => `${API_BASE}/lens/customer/${ticketId}`,
};

// ─── Portal (customer-facing) ─────────────────────────────────
export const PORTAL = {
  tickets: `${API_BASE}/portal/tickets`,
  getTicket: (id: string) => `${API_BASE}/portal/tickets/${id}`,
  comments: (id: string) => `${API_BASE}/portal/tickets/${id}/comments`,
  csat: (id: string) => `${API_BASE}/portal/tickets/${id}/csat`,
};

// ─── API Tokens ───────────────────────────────────────────────
// Plaintext is shown ONCE on create; there is no reveal endpoint (the server
// stores only a hash). Lists return a masked representation.
export const API_TOKENS = {
  list: `${API_BASE}/api-tokens`,
  create: `${API_BASE}/api-tokens`,
  update: (id: string) => `${API_BASE}/api-tokens/${id}`,
  revoke: (id: string) => `${API_BASE}/api-tokens/${id}`,
};

// ─── Webhooks (outbound endpoint management) ──────────────────
// Mounted on /v1/webhook-endpoints (NOT /v1/webhooks, which is the inbound
// receiver with a raw-body parser). Each endpoint has its own signing secret.
export const WEBHOOKS = {
  list: `${API_BASE}/webhook-endpoints`,
  create: `${API_BASE}/webhook-endpoints`,
  update: (id: string) => `${API_BASE}/webhook-endpoints/${id}`,
  delete: (id: string) => `${API_BASE}/webhook-endpoints/${id}`,
  test: (id: string) => `${API_BASE}/webhook-endpoints/${id}/test`,
  rotateSecret: (id: string) => `${API_BASE}/webhook-endpoints/${id}/rotate-secret`,
  signingSecret: `${API_BASE}/webhook-endpoints/signing-secret`,
};

// ─── Integrations status (honest "configured?" flags) ─────────
export const INTEGRATIONS_STATUS = `${API_BASE}/org/settings/integrations-status`;

// ─── Health ───────────────────────────────────────────────────
export const HEALTH = {
  liveness: `${TICKETS_BASE_URL}/health`,
  readiness: `${TICKETS_BASE_URL}/health/deps`,
};
